import { PHASE_LABELS, type RunState } from "../types/api";
import { humanizeSpecialty } from "./format";

const TOOL_LABELS: Record<string, string> = {
  rewrite_query_tool: "Structuring your question (PICOT / SPICE)",
  classify_query_tool: "Routing to clinical specialists",
  deduplicate_papers_tool: "Deduplicating evidence",
  enrich_citations_tool: "Enriching citations and journal quality",
  generate_followup_tool: "Generating follow-up questions",
  cci_estimate_tool: "Estimating compute footprint",
};

function specialistKeyFromTool(name: string): string | null {
  if (name.endsWith("_specialist")) return name.slice(0, -"_specialist".length);
  if (name.startsWith("search_") && name.endsWith("_papers")) {
    return name.slice("search_".length, -"_papers".length);
  }
  return null;
}

function toolLabel(name: string): string {
  if (TOOL_LABELS[name]) return TOOL_LABELS[name];
  const spec = specialistKeyFromTool(name);
  if (spec) return `Consulting ${humanizeSpecialty(spec)} specialist`;
  return humanizeSpecialty(name.replace(/_/g, " "));
}

export interface LiveStatus {
  headline: string;
  detail: string | null;
  waiting: boolean;
}

export function deriveLiveStatus(state: RunState, nowMs: number): LiveStatus {
  if (state.runStatus === "connecting") {
    return {
      headline: "Connecting to clinical search",
      detail: "Opening live stream…",
      waiting: true,
    };
  }

  if (state.runStatus !== "running") {
    return { headline: "", detail: null, waiting: false };
  }

  const activePhaseIdx = state.phaseStatus.findIndex((s) => s === "active");
  const phaseName = activePhaseIdx >= 0 ? PHASE_LABELS[activePhaseIdx] : null;

  if (state.activeTool) {
    return {
      headline: toolLabel(state.activeTool),
      detail: phaseName ? `Phase ${activePhaseIdx + 1} · ${phaseName}` : null,
      waiting: false,
    };
  }

  if (state.supervisorAnswer && activePhaseIdx === 4) {
    return {
      headline: "Synthesizing recommendation",
      detail: "Streaming final answer…",
      waiting: false,
    };
  }

  const specialistKeys = Object.keys(state.specialistText);
  const streamingSpec = specialistKeys.find((k) => {
    const spec = state.specialistText[k];
    return spec.text.length > 0 && state.phaseStatus[2] === "active";
  });
  if (streamingSpec) {
    return {
      headline: `${humanizeSpecialty(streamingSpec)} is analyzing evidence`,
      detail: "Streaming specialist notes…",
      waiting: false,
    };
  }

  if (state.detectedDomains.length > 0 && state.phaseStatus[2] !== "done") {
    const names = state.detectedDomains.map(humanizeSpecialty).join(", ");
    return {
      headline: "Preparing specialist consultations",
      detail: names,
      waiting: true,
    };
  }

  if (state.rewrittenQuery && state.phaseStatus[0] === "done" && state.phaseStatus[1] !== "done") {
    return {
      headline: "Classifying clinical domains",
      detail: state.rewrittenQuery.length > 100
        ? `${state.rewrittenQuery.slice(0, 100)}…`
        : state.rewrittenQuery,
      waiting: true,
    };
  }

  if (phaseName) {
    const phaseMessages: Record<string, LiveStatus> = {
      Rewrite: {
        headline: "Structuring your question",
        detail: "Applying PICOT / SPICE framework…",
        waiting: true,
      },
      Classify: {
        headline: "Classifying clinical domains",
        detail: "Matching query to specialist areas…",
        waiting: true,
      },
      Specialists: {
        headline: "Consulting clinical specialists",
        detail: "Searching literature and drafting perspectives…",
        waiting: true,
      },
      Retrieve: {
        headline: "Retrieving and ranking evidence",
        detail: state.paperCount > 0 ? `${state.paperCount} papers found so far` : null,
        waiting: true,
      },
      Synthesize: {
        headline: "Synthesizing recommendation",
        detail: "Combining specialist findings…",
        waiting: true,
      },
    };
    const msg = phaseMessages[phaseName];
    if (msg) return msg;
  }

  const staleMs = state.lastEventMs != null ? nowMs - state.lastEventMs : 0;
  if (staleMs > 2500) {
    return {
      headline: "Waiting for model response",
      detail: "Large language models can take 10–30s between steps",
      waiting: true,
    };
  }

  return {
    headline: "Clinical search in progress",
    detail: state.logEventCount > 0 ? `${state.logEventCount} events received` : "Starting pipeline…",
    waiting: true,
  };
}

export function humanizeLogType(type: string): string {
  const map: Record<string, string> = {
    run_start: "run started",
    query_rewritten: "query structured",
    query_classified: "domains classified",
    tool_start: "tool started",
    tool_running: "tool running",
    tool_result: "tool result",
    tool_end: "tool done",
    specialist_papers: "papers retrieved",
    specialist_text: "specialist writing",
    specialist_tool: "specialist tool",
    text: "synthesis stream",
    thinking: "model thinking",
    papers: "papers merged",
    consensus: "consensus parsed",
    followups: "follow-ups",
    run_end: "run complete",
    done: "finished",
    error: "error",
    warning: "warning",
  };
  return map[type] ?? type.replace(/_/g, " ");
}

export function humanizeLogSummary(row: { type: string; summary: string }): string {
  const s = row.summary;
  if (row.type === "tool_running" || row.type === "tool_start" || row.type === "tool_end") {
    return toolLabel(s) || s;
  }
  if (row.type === "specialist_text" && s.includes(" ")) {
    const [spec] = s.split(" ");
    return `${humanizeSpecialty(spec)} writing…`;
  }
  if (row.type === "specialist_papers") {
    const m = s.match(/^(\S+)\s·\s(\d+)/);
    if (m) return `${humanizeSpecialty(m[1])} · ${m[2]} papers`;
  }
  if (row.type === "query_rewritten" && s) return s;
  if (row.type === "text") return "streaming synthesis";
  if (row.type === "thinking") return "reasoning…";
  return s;
}
